import type {
  InventoryAvailability,
  InventoryFamily,
  InventoryLifecycle,
} from "../api/types";
import { AVAILABILITY_LABELS, FAMILY_LABELS } from "./labels";

/**
 * Finding a product on a shelf, and narrowing what is on show.
 *
 * Every control here submits to the backend. None of them filters the page in
 * the browser: the list is paginated server-side, so a local filter would
 * search the twenty-five rows in front of the operator and quietly report that
 * an item on page two does not exist.
 *
 * ONE SEARCH BOX, THREE FIELDS
 * The backend matches the product name, the shop's own product code and the
 * variant name in a single pass. Splitting it into three would ask somebody
 * holding a product to decide which field the thing in their hand is.
 */

const FAMILIES: InventoryFamily[] = [
  "glasses",
  "contact_lenses",
  "accessories",
];

const AVAILABILITIES: InventoryAvailability[] = [
  "in_stock",
  "low_stock",
  "out_of_stock",
];

/** What each lifecycle scope is CALLED on screen.
 *
 * "Operational" and deliberately not "On sale". This shop already uses "on
 * sale" for one thing — a product carrying a discount — and it is rendered as
 * a badge two screens away, on the product summary table. A filter that used
 * the same words for "currently published" would read as "show me the
 * discounted stock", which is a different question with a different answer,
 * and an operator would have no way to tell which one they had asked.
 *
 * The stored values are untouched: `operational`, `unpublished` and `archived`
 * are what the backend filters on, and only the label moved.
 */
const LIFECYCLES: { value: InventoryLifecycle; label: string }[] = [
  { value: "operational", label: "Operational" },
  { value: "unpublished", label: "Unpublished" },
  { value: "archived", label: "Archived (read only)" },
];

export function InventoryToolbar({
  search,
  family,
  availability,
  lifecycle,
  total,
  onSearch,
  onFamily,
  onAvailability,
  onLifecycle,
  onClear,
}: {
  search: string;
  family: string;
  availability: string;
  lifecycle: InventoryLifecycle;
  /** null until the backend has answered, so no count is shown while loading. */
  total: number | null;
  onSearch: (next: string) => void;
  onFamily: (next: string) => void;
  onAvailability: (next: string) => void;
  onLifecycle: (next: InventoryLifecycle) => void;
  onClear: () => void;
}) {
  const narrowed = Boolean(
    search || family || availability || lifecycle !== "operational",
  );

  return (
    <div className="inventory__toolbar">
      <div className="inventory__search">
        <label className="sr-only" htmlFor="inventory-search">
          Search stock by product name, product code or variant name
        </label>
        <input
          id="inventory-search"
          type="search"
          placeholder="Product name, product code or variant"
          // Uncontrolled with a key, so typing is never interrupted by a
          // refetch and clearing the filters genuinely empties the box.
          key={`search-${search}`}
          defaultValue={search}
          onKeyDown={(event) => {
            if (event.key === "Enter") onSearch(event.currentTarget.value);
          }}
          onBlur={(event) => {
            if (event.currentTarget.value !== search) {
              onSearch(event.currentTarget.value);
            }
          }}
        />
      </div>

      <div className="inventory__filter">
        <label className="sr-only" htmlFor="inventory-family">
          Product family
        </label>
        <select
          id="inventory-family"
          value={family}
          onChange={(event) => onFamily(event.target.value)}
        >
          <option value="">All families</option>
          {FAMILIES.map((value) => (
            <option key={value} value={value}>
              {FAMILY_LABELS[value]}
            </option>
          ))}
        </select>
      </div>

      <div className="inventory__filter">
        <label className="sr-only" htmlFor="inventory-availability">
          Availability
        </label>
        <select
          id="inventory-availability"
          value={availability}
          onChange={(event) => onAvailability(event.target.value)}
        >
          <option value="">All availability</option>
          {AVAILABILITIES.map((value) => (
            <option key={value} value={value}>
              {AVAILABILITY_LABELS[value]}
            </option>
          ))}
        </select>
      </div>

      {/* Not "all products". Stock does not cease to exist because a product
          was hidden or retired, so those rows are reachable — but they are a
          different question from "what am I selling", and mixing them into one
          list would put an archived item beside a live one with nothing but a
          disabled button to tell them apart. */}
      <div className="inventory__filter">
        <label className="sr-only" htmlFor="inventory-lifecycle">
          Lifecycle
        </label>
        <select
          id="inventory-lifecycle"
          value={lifecycle}
          onChange={(event) =>
            onLifecycle(event.target.value as InventoryLifecycle)
          }
        >
          {LIFECYCLES.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="spacer" />

      {narrowed ? (
        <button type="button" className="btn btn--sm" onClick={onClear}>
          Clear filters
        </button>
      ) : null}

      {total !== null ? (
        <span className="muted num" data-testid="inventory-total">
          {total} item{total === 1 ? "" : "s"}
        </span>
      ) : null}
    </div>
  );
}
